import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import HRCampusSelector from "../../components/HRCampusSelector";
import { getCurrentUser } from "../../auth";

const EmployeeList = () => {
  const [selectedCampus, setSelectedCampus] = useState(() => {
    try { return localStorage.getItem("hr_emp_campus") || "bulacan"; } catch { return "bulacan"; }
  });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    position: '',
    department: '',
    salary: '',
    overtimeRate: '',
    substitutionRate: '',
    allowance: '',
    sss: '',
    philhealth: '',
    pagibig: '',
    tax: '',
    booksMiscMonthly: '',
    coopMonthly: '',
    uniformMonthly: '',
    cashAdvanceMonthly: '',
    contriMonthly: '',
    otherDeduction: '',
    email: '',
    phone: '',
    campus: 'bulacan',
  });
  const [editId, setEditId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputId = "employee_csv_input";

  function lsKey(campus) {
    return `employees_${String(campus || 'gen').toLowerCase()}`;
  }
  function loadLocalEmployees(campus) {
    try {
      const raw = localStorage.getItem(lsKey(campus));
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }
  function saveLocalEmployees(campus, list) {
    try {
      localStorage.setItem(lsKey(campus), JSON.stringify(list));
    } catch (err) {
      console.warn("Failed to save employees locally");
    }
  }

  function normalizeCampusLabel(x) {
    const s = String(x || "").toLowerCase();
    if (s.includes("gapan")) return "gapan";
    if (s.includes("bulacan")) return "bulacan";
    return s;
  }
  const currentUser = getCurrentUser();
  const roleStr = String(currentUser?.role || "").toLowerCase();
  const rolesArr = Array.isArray(currentUser?.roles) ? currentUser.roles.map(r => String(r).toLowerCase()) : [];
  const hasRole = (r) => roleStr === r || rolesArr.includes(r);
  const isExec = hasRole("admin") || hasRole("superadmin") || hasRole("vicepresident") || hasRole("vp") || hasRole("board") || hasRole("bot") || String(currentUser?.campus || "").toLowerCase() === "all";
  useEffect(() => {
    try { localStorage.setItem("hr_emp_campus", selectedCampus); } catch (e) { void e; }
  }, [selectedCampus]);
  useEffect(() => {
    if (isExec && selectedCampus !== "") setSelectedCampus("");
  }, [isExec]);
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const empCollection = collection(db, 'employees');
        const empSnapshot = await getDocs(empCollection);
        let empList = empSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (!isExec || selectedCampus) {
          const sc = normalizeCampusLabel(selectedCampus);
          empList = empList.filter(emp => normalizeCampusLabel(emp.campus) === sc);
        }
        setEmployees(empList);
        setLoading(false);
      } catch (error) {
        let local = loadLocalEmployees(selectedCampus);
        if (!isExec || selectedCampus) {
          const sc = normalizeCampusLabel(selectedCampus);
          local = local.filter(emp => normalizeCampusLabel(emp.campus) === sc);
        }
        setEmployees(local);
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [selectedCampus, isExec]);

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddOrEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, campus: form.campus || selectedCampus };
      if (editId) {
        await updateDoc(doc(db, 'employees', editId), payload);
      } else {
        await addDoc(collection(db, 'employees'), payload);
      }
      setShowForm(false);
      setEditId(null);
      setForm({ name: '', position: '', department: '', salary: '', email: '', phone: '', campus: selectedCampus });
      setLoading(true);
      const empCollection = collection(db, 'employees');
      const empSnapshot = await getDocs(empCollection);
      const empList = empSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEmployees(empList.filter(emp => emp.campus === selectedCampus));
      setLoading(false);
    } catch (error) {
      const payload = { ...form, campus: form.campus || selectedCampus };
      if (editId) {
        const next = employees.map(emp => emp.id === editId ? { ...payload, id: editId } : emp);
        setEmployees(next);
        saveLocalEmployees(selectedCampus, next);
      } else {
        const id = `LS-${Date.now()}`;
        const next = [...employees, { ...payload, id }];
        setEmployees(next);
        saveLocalEmployees(selectedCampus, next);
      }
      setShowForm(false);
      setEditId(null);
    }
  };

  const handleEdit = (emp) => {
    setForm({
      name: emp.name || '',
      position: emp.position || '',
      department: emp.department || '',
      salary: emp.salary || '',
      overtimeRate: emp.overtimeRate || '',
      substitutionRate: emp.substitutionRate || '',
      allowance: emp.allowance || '',
      sss: emp.sss || '',
      philhealth: emp.philhealth || '',
      pagibig: emp.pagibig || '',
      tax: emp.tax || '',
      booksMiscMonthly: emp.booksMiscMonthly || '',
      coopMonthly: emp.coopMonthly || '',
      uniformMonthly: emp.uniformMonthly || '',
      cashAdvanceMonthly: emp.cashAdvanceMonthly || '',
      contriMonthly: emp.contriMonthly || '',
      otherDeduction: emp.otherDeduction || '',
      email: emp.email || '',
      phone: emp.phone || '',
      campus: emp.campus || selectedCampus
    });
    setEditId(emp.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this employee?')) {
      try {
        await deleteDoc(doc(db, 'employees', id));
        setEmployees(employees.filter(emp => emp.id !== id));
      } catch {
        const next = employees.filter(emp => emp.id !== id);
        setEmployees(next);
        saveLocalEmployees(selectedCampus, next);
      }
    }
  };

  function downloadTemplate() {
    const headers = [
      "name","position","department","salary","email","phone","campus",
      "overtimeRate","substitutionRate","allowance","sss","philhealth","pagibig","tax",
      "booksMiscMonthly","coopMonthly","uniformMonthly","cashAdvanceMonthly","contriMonthly","otherDeduction"
    ];
    const sample = [
      "Juan Dela Cruz","Teacher","Elementary","18000","juan@example.com","09171234567","bulacan",
      "100","150","500","300","400","200","0",
      "0","0","0","0","0","0"
    ];
    const csv = [headers.join(","), sample.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vmsc_employees_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function normalizeNumber(v) {
    if (v == null) return 0;
    const s = String(v)
      .replace(/₱/g, "")
      .replace(/,/g, "")
      .replace(/\s+/g, "")
      .replace(/[^\d.-]/g, ""); // keep digits, dot, minus
    const n = Number(s);
    return isNaN(n) ? 0 : n;
  }

  function getField(obj, aliases) {
    for (const name of aliases) {
      if (Object.prototype.hasOwnProperty.call(obj, name)) {
        return obj[name];
      }
    }
    return undefined;
  }

  function parseCSV(text) {
    const rows = [];
    let i = 0, field = "", row = [], inQuotes = false;
    while (i < text.length) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else { inQuotes = false; }
        } else {
          field += ch;
        }
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ',') { row.push(field); field = ""; }
        else if (ch === '\n' || ch === '\r') {
          if (field !== "" || row.length) { row.push(field); rows.push(row); row = []; field = ""; }
          // handle \r\n
          if (ch === '\r' && text[i + 1] === '\n') i++;
        } else {
          field += ch;
        }
      }
      i++;
    }
    if (field !== "" || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  async function handleCSVFile(file) {
    setImporting(true);
    try {
      const content = await file.text();
      const rows = parseCSV(content).filter(r => r.length && r.some(c => String(c).trim() !== ""));
      if (rows.length < 2) throw new Error("No data rows");
      const headers = rows[0].map(h => String(h).trim().toLowerCase());
      const list = [];
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        const obj = {};
        headers.forEach((h, idx) => { obj[h] = row[idx]; });
        const salaryRaw = getField(obj, ["salary","monthly salary","monthly_salary","basic salary","basic_salary","monthlybasic","monthly basic"]);
        const overtimeRaw = getField(obj, ["overtimerate","overtime rate","overtime_rate"]);
        const substRaw = getField(obj, ["substitutionrate","substitution rate","substitution_rate"]);
        const allowanceRaw = getField(obj, ["allowance","allowances"]);
        const sssRaw = getField(obj, ["sss"]);
        const philRaw = getField(obj, ["philhealth","phil health","phil_health"]);
        const pagibigRaw = getField(obj, ["pagibig","pag-ibig","pag_ibig"]);
        const taxRaw = getField(obj, ["tax","withholding tax","withholding_tax"]);
        const booksRaw = getField(obj, ["booksmiscmonthly","books/misc monthly","books_misc_monthly"]);
        const coopRaw = getField(obj, ["coopmonthly","coop monthly","coop_monthly"]);
        const uniformRaw = getField(obj, ["uniformmonthly","uniform monthly","uniform_monthly"]);
        const cashAdvRaw = getField(obj, ["cashadvancemonthly","cash advance monthly","cash_advance_monthly"]);
        const contriRaw = getField(obj, ["contrimonthly","contri monthly","contri_monthly"]);
        const otherDedRaw = getField(obj, ["otherdeduction","other deduction","other_deduction"]);
        const payload = {
          name: obj.name || "",
          position: obj.position || "",
          department: obj.department || "",
          salary: normalizeNumber(salaryRaw),
          email: obj.email || "",
          phone: obj.phone || "",
          campus: String(obj.campus || selectedCampus).toLowerCase(),
          overtimeRate: normalizeNumber(overtimeRaw),
          substitutionRate: normalizeNumber(substRaw),
          allowance: normalizeNumber(allowanceRaw),
          sss: normalizeNumber(sssRaw),
          philhealth: normalizeNumber(philRaw),
          pagibig: normalizeNumber(pagibigRaw),
          tax: normalizeNumber(taxRaw),
          booksMiscMonthly: normalizeNumber(booksRaw),
          coopMonthly: normalizeNumber(coopRaw),
          uniformMonthly: normalizeNumber(uniformRaw),
          cashAdvanceMonthly: normalizeNumber(cashAdvRaw),
          contriMonthly: normalizeNumber(contriRaw),
          otherDeduction: normalizeNumber(otherDedRaw),
        };
        list.push(payload);
      }
      const empCol = collection(db, 'employees');
      let updated = 0, created = 0;
      try {
        // Build index of existing employees by email+campus or name+campus
        const snap0 = await getDocs(empCol);
        const existing = snap0.docs.map(d => ({ id: d.id, ...d.data() }));
        const index = new Map();
        const normStr = (s) => String(s || "").toLowerCase().trim();
        const normName = (s) => normStr(s).replace(/\s+/g, " ");
        existing.forEach(e => {
          const campusKey = normStr(e.campus);
          const emailKey = normStr(e.email);
          const nameKey = normName(e.name);
          if (emailKey) index.set(`email:${emailKey}|campus:${campusKey}`, e.id);
          if (nameKey) index.set(`name:${nameKey}|campus:${campusKey}`, e.id);
        });
        // Upsert each row
        for (const p of list) {
          const campusKey = normStr(p.campus || selectedCampus);
          const emailKey = normStr(p.email);
          const nameKey = normName(p.name);
          const key = emailKey ? `email:${emailKey}|campus:${campusKey}` : `name:${nameKey}|campus:${campusKey}`;
          const foundId = index.get(key);
          if (foundId) {
            await updateDoc(doc(db, 'employees', foundId), p);
            updated++;
          } else {
            await addDoc(empCol, p);
            created++;
          }
        }
      } catch (e) {
        // Local storage fallback with upsert
        const prev = loadLocalEmployees(selectedCampus);
        const normStr = (s) => String(s || "").toLowerCase().trim();
        const normName = (s) => normStr(s).replace(/\s+/g, " ");
        const match = (a, b) => {
          const campusA = normStr(a.campus), campusB = normStr(b.campus);
          const emailA = normStr(a.email), emailB = normStr(b.email);
          const nameA = normName(a.name), nameB = normName(b.name);
          if (emailA && emailB) return emailA === emailB && campusA === campusB;
          return nameA === nameB && campusA === campusB;
        };
        const merged = [...prev];
        list.forEach(p => {
          const i = merged.findIndex(x => match(x, p));
          if (i >= 0) { merged[i] = { ...merged[i], ...p }; updated++; }
          else { merged.push({ ...p, id: `LS-${Date.now()}-${Math.random()}` }); created++; }
        });
        saveLocalEmployees(selectedCampus, merged);
        setEmployees(merged.filter(emp => emp.campus === selectedCampus));
      }
      // Refresh from Firestore if available
      try {
        const snap = await getDocs(empCol);
        const refreshed = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(emp => emp.campus === selectedCampus);
        if (refreshed.length) setEmployees(refreshed);
      } catch (e) { void e; }
      alert(`Upsert complete — Updated: ${updated}, Created: ${created}.`);
    } catch (err) {
      alert("Failed to import CSV. Please check the file format.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="container-fluid px-4 py-3">
      <HRCampusSelector selectedCampus={selectedCampus} onChange={setSelectedCampus} allowAll={isExec} />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Employee Management</h2>
        <div className="d-flex gap-2">
          <input id={fileInputId} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) handleCSVFile(f); e.target.value = ""; }} />
          <button className="btn btn-outline-primary" disabled={importing} onClick={() => document.getElementById(fileInputId)?.click()}>
            {importing ? "Importing..." : "Import CSV"}
          </button>
          <button
            className="btn btn-outline-secondary ms-2"
            onClick={async () => {
              try {
                const empSnap = await getDocs(collection(db, "employees"));
                const have = new Map(empSnap.docs.map(d => {
                  const e = d.data() || {};
                  return [`${String(e.name||"").trim().toLowerCase()}|${String(e.campus||"").toLowerCase()}`, true];
                }));
                const attSnap = await getDocs(collection(db, "employees_attendance"));
                const toCreate = [];
                attSnap.docs.forEach(d => {
                  const a = d.data() || {};
                  const nm = String(a.employeeName || a.employee || "").trim();
                  const cp = normalizeCampusLabel(a.campus || selectedCampus || "");
                  if (!nm) return;
                  const okCampus = isExec && !selectedCampus ? true : (cp === normalizeCampusLabel(selectedCampus));
                  const key = `${nm.toLowerCase()}|${cp}`;
                  if (okCampus && !have.has(key)) {
                    have.set(key, true);
                    toCreate.push({ name: nm, campus: cp });
                  }
                });
                for (const emp of toCreate) {
                  const id = `${emp.campus}|${emp.name.toLowerCase().replace(/\s+/g,"_")}`;
                  await setDoc(doc(db, "employees", id), { ...emp, position:"",department:"",salary:0,overtimeRate:0,substitutionRate:0,allowance:0,sss:0,philhealth:0,pagibig:0,tax:0,booksMiscMonthly:0,coopMonthly:0,uniformMonthly:0,cashAdvanceMonthly:0,contriMonthly:0,otherDeduction:0,email:"",phone:"" }, { merge: true });
                }
                const snap2 = await getDocs(collection(db, "employees"));
                let list = snap2.docs.map(d => ({ id: d.id, ...d.data() }));
                if (!isExec || selectedCampus) {
                  const sc = normalizeCampusLabel(selectedCampus);
                  list = list.filter(e => normalizeCampusLabel(e.campus) === sc);
                }
                setEmployees(list);
                alert(`Synced ${toCreate.length} employee(s) from attendance.`);
              } catch (e) {
                alert("Failed to sync from attendance. Please check your connection and permissions.");
              }
            }}
            title="Backfill employees from recorded attendance entries"
          >
            Sync From Attendance
          </button>
          <button className="btn btn-outline-secondary" onClick={downloadTemplate}>Download CSV Template</button>
          <button
            className="btn btn-outline-secondary"
            onClick={async () => {
              try {
                const empCollection = collection(db, 'employees');
                const empSnapshot = await getDocs(empCollection);
                const empList = empSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
                const target = empList.filter(e => (e.campus || "").toLowerCase() !== (e.campus || "").trim());
                for (const e of empList) {
                  const campus = String(e.campus || selectedCampus).toLowerCase().trim();
                  await updateDoc(doc(db, 'employees', e.id), { campus });
                }
                const refreshed = await getDocs(empCollection);
                const refreshedList = refreshed.docs.map(d => ({ id: d.id, ...d.data() }));
                let filtered = refreshedList;
                if (!isExec || selectedCampus) {
                  const sc = normalizeCampusLabel(selectedCampus);
                  filtered = refreshedList.filter(emp => normalizeCampusLabel(emp.campus) === sc);
                }
                setEmployees(filtered);
                alert("Campus normalized to lowercase for all employees.");
              } catch (err) {
                alert("Failed to normalize campuses. Please check connectivity.");
              }
            }}
            title="Lower-case campus for all employees to avoid filter mismatches"
          >
            Normalize Campus
          </button>
          <button
            className="btn btn-outline-secondary ms-2"
            onClick={async () => {
              try {
                const empSnap = await getDocs(collection(db, "employees"));
                const have = new Map(empSnap.docs.map(d => {
                  const e = d.data() || {};
                  return [`${String(e.name||"").trim().toLowerCase()}|${String(e.campus||"").toLowerCase()}`, true];
                }));
                const paySnap = await getDocs(collection(db, "payroll"));
                const toCreate = [];
                paySnap.docs.forEach(d => {
                  const p = d.data() || {};
                  const nm = String(p.employeeName || "").trim();
                  const cp = normalizeCampusLabel(p.campus || selectedCampus || "");
                  if (!nm) return;
                  const okCampus = isExec && !selectedCampus ? true : (cp === normalizeCampusLabel(selectedCampus));
                  const key = `${nm.toLowerCase()}|${cp}`;
                  if (okCampus && !have.has(key)) {
                    have.set(key, true);
                    toCreate.push({ name: nm, campus: cp, salary: Number(p.basicPay ? p.basicPay * 2 : 0) });
                  }
                });
                for (const emp of toCreate) {
                  const id = `${emp.campus}|${emp.name.toLowerCase().replace(/\s+/g,"_")}`;
                  await setDoc(doc(db, "employees", id), { ...emp, position:"",department:"",overtimeRate:0,substitutionRate:0,allowance:0,sss:0,philhealth:0,pagibig:0,tax:0,booksMiscMonthly:0,coopMonthly:0,uniformMonthly:0,cashAdvanceMonthly:0,contriMonthly:0,otherDeduction:0,email:"",phone:"" }, { merge: true });
                }
                const snap2 = await getDocs(collection(db, "employees"));
                let list = snap2.docs.map(d => ({ id: d.id, ...d.data() }));
                if (!isExec || selectedCampus) {
                  const sc = normalizeCampusLabel(selectedCampus);
                  list = list.filter(e => normalizeCampusLabel(e.campus) === sc);
                }
                setEmployees(list);
                alert(`Synced ${toCreate.length} employee(s) from payroll.`);
              } catch (e) {
                alert("Failed to sync from payroll. Please check your connection and permissions.");
              }
            }}
            title="Backfill employees from existing payroll entries"
          >
            Sync From Payroll
          </button>
          <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditId(null); setForm({ name: '', position: '', department: '', salary: '', email: '', phone: '', campus: selectedCampus }); setTimeout(()=>{ try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (err) { void 0; } }, 0); }}>
            <i className="bi bi-plus-circle me-2"></i>Add Employee
          </button>
        </div>
      </div>
      {showForm && (
        <div className="card shadow-sm mb-4">
          <form onSubmit={handleAddOrEdit}>
            <div className="card-header fw-bold">Add Employee</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <input className="form-control" name="name" placeholder="Name" value={form.name} onChange={handleFormChange} required />
                </div>
                <div className="col-md-3">
                  <input className="form-control" name="position" placeholder="Position" value={form.position} onChange={handleFormChange} required />
                </div>
                <div className="col-md-3">
                  <input className="form-control" name="department" placeholder="Department" value={form.department} onChange={handleFormChange} required />
                </div>
                <div className="col-md-3">
                  <input className="form-control" name="salary" placeholder="Salary" type="number" value={form.salary} onChange={handleFormChange} required />
                </div>
                <div className="col-md-3">
                  <select className="form-select" name="campus" value={form.campus} onChange={handleFormChange} required>
                    <option value="bulacan">Bulacan</option>
                    <option value="gapan">Gapan</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <input className="form-control" name="email" placeholder="Email" value={form.email} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <input className="form-control" name="phone" placeholder="Phone" value={form.phone} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Overtime Rate</label>
                  <input type="number" className="form-control" name="overtimeRate" value={form.overtimeRate} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Substitution Rate</label>
                  <input type="number" className="form-control" name="substitutionRate" value={form.substitutionRate} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Allowance</label>
                  <input type="number" className="form-control" name="allowance" value={form.allowance} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">SSS</label>
                  <input type="number" className="form-control" name="sss" value={form.sss} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">PhilHealth</label>
                  <input type="number" className="form-control" name="philhealth" value={form.philhealth} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Pag-IBIG</label>
                  <input type="number" className="form-control" name="pagibig" value={form.pagibig} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tax</label>
                  <input type="number" className="form-control" name="tax" value={form.tax} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Books & Misc (Monthly)</label>
                  <input type="number" className="form-control" name="booksMiscMonthly" value={form.booksMiscMonthly} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">COOP (Monthly)</label>
                  <input type="number" className="form-control" name="coopMonthly" value={form.coopMonthly} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Uniform (Monthly)</label>
                  <input type="number" className="form-control" name="uniformMonthly" value={form.uniformMonthly} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Cash Advance (Monthly)</label>
                  <input type="number" className="form-control" name="cashAdvanceMonthly" value={form.cashAdvanceMonthly} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Contri (Monthly)</label>
                  <input type="number" className="form-control" name="contriMonthly" value={form.contriMonthly} onChange={handleFormChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Other Deduction</label>
                  <input type="number" className="form-control" name="otherDeduction" value={form.otherDeduction} onChange={handleFormChange} />
                </div>
              </div>
            </div>
            <div className="card-footer d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-success">{editId ? 'Update' : 'Add'}</button>
            </div>
          </form>
        </div>
      )}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Department</th>
                  <th>Salary</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="text-center py-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></td></tr>
                ) : employees.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-4 text-muted">No employees found</td></tr>
                ) : (
                  employees.map(emp => (
                    <tr key={emp.id}>
                      <td>{emp.name}</td>
                      <td>{emp.position}</td>
                      <td>{emp.department}</td>
                      <td>₱{emp.salary}</td>
                      <td>{emp.email}</td>
                      <td>{emp.phone}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEdit(emp)}><i className="bi bi-pencil"></i></button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(emp.id)}><i className="bi bi-trash"></i></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showForm && (
        <div
          className="modal show"
          style={{
            display: 'block',
            background: 'rgba(0,0,0,0.3)',
            position: 'fixed',
            inset: 0,
            zIndex: 1050
          }}
          aria-modal="true"
          role="dialog"
        >
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleAddOrEdit}>
              <div className="modal-header">
                <h5 className="modal-title">{editId ? 'Edit Employee' : 'Add Employee'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowForm(false)}></button>
              </div>
              <div className="modal-body">
                <input className="form-control mb-2" name="name" placeholder="Name" value={form.name} onChange={handleFormChange} required />
                <input className="form-control mb-2" name="position" placeholder="Position" value={form.position} onChange={handleFormChange} required />
                <input className="form-control mb-2" name="department" placeholder="Department" value={form.department} onChange={handleFormChange} required />
                <input className="form-control mb-2" name="salary" placeholder="Salary" type="number" value={form.salary} onChange={handleFormChange} required />
                <div className="mb-2">
                  <label className="form-label">Campus</label>
                  <select className="form-select" name="campus" value={form.campus} onChange={handleFormChange} required>
                    <option value="bulacan">Bulacan</option>
                    <option value="gapan">Gapan</option>
                  </select>
                </div>
                <input className="form-control mb-2" name="email" placeholder="Email" value={form.email} onChange={handleFormChange} />
                <input className="form-control mb-2" name="phone" placeholder="Phone" value={form.phone} onChange={handleFormChange} />
                <div className="mb-3">
                  <label className="form-label">Overtime Rate</label>
                  <input type="number" className="form-control" name="overtimeRate" value={form.overtimeRate} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Allowance</label>
                  <input type="number" className="form-control" name="allowance" value={form.allowance} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">SSS Deduction</label>
                  <input type="number" className="form-control" name="sss" value={form.sss} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">PhilHealth Deduction</label>
                  <input type="number" className="form-control" name="philhealth" value={form.philhealth} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Pag-IBIG Deduction</label>
                  <input type="number" className="form-control" name="pagibig" value={form.pagibig} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Tax</label>
                  <input type="number" className="form-control" name="tax" value={form.tax} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Books & Misc (Monthly)</label>
                  <input type="number" className="form-control" name="booksMiscMonthly" value={form.booksMiscMonthly} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">COOP (Monthly)</label>
                  <input type="number" className="form-control" name="coopMonthly" value={form.coopMonthly} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Uniform (Monthly)</label>
                  <input type="number" className="form-control" name="uniformMonthly" value={form.uniformMonthly} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Cash Advance (Monthly)</label>
                  <input type="number" className="form-control" name="cashAdvanceMonthly" value={form.cashAdvanceMonthly} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Contri (Monthly)</label>
                  <input type="number" className="form-control" name="contriMonthly" value={form.contriMonthly} onChange={handleFormChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Other Deduction</label>
                  <input type="number" className="form-control" name="otherDeduction" value={form.otherDeduction} onChange={handleFormChange} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-success">{editId ? 'Update' : 'Add'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;
